import { animate, style, transition, trigger } from '@angular/animations';
import {
    ChangeDetectionStrategy,
    Component,
    EventEmitter,
    Input,
    OnChanges,
    Output,
    QueryList,
    TemplateRef
} from '@angular/core';
import { DropdownType, NavigationItem } from '../../../models/common';

@Component({
    selector: 'lsq-dropdown-navigation',
    template: `
        <div class="animation-wrapper">
            <div
                *ngIf="isShowing && (displayItems?.length || prependItems || appendItems)"
                class="lsq-dropdown-container"
                [ngClass]="{
                    'has-header': header,
                    'bg-light': background === 'light' || background === 'white'
                }"
                [@slide]
                [@.disabled]="!shouldAnimate"
            >
                <ul class="lsq-dropdown-list list-unstyled">
                    <ng-container *ngFor="let templateRef of prependItems">
                        <ng-container
                            *ngTemplateOutlet="templateRef; context: { eventEmitter: select }"
                        ></ng-container>
                    </ng-container>
                    <ng-container *ngFor="let item of displayItems; let i = index">
                        <ng-container *ngIf="isLoggedIn || item.allowAnonymous">
                            <ng-container *claimsOr="item.claims">
                                <ng-container *featureFlag="item.featureFlags">
                                    <ng-container *environments="item.environments">
                                        <li
                                            *ngIf="!item.hideInDesktop"
                                            [id]="createId(item.label, i)"
                                            class="lsq-dropdown-item lsq-dropdown-navigation-item"
                                            [routerLinkActive]="item.routerLinkActive"
                                            [routerLinkActiveOptions]="
                                                item.routerLinkActiveOptions || { exact: true }
                                            "
                                        >
                                            <a
                                                *ngIf="!item.externalLink"
                                                [routerLink]="item.routerLink"
                                                (click)="select.emit($event)"
                                            >
                                                <span
                                                    [ngClass]="{
                                                        'is-child': item.isChild,
                                                        'is-grandchild': item.isGrandChild
                                                    }"
                                                    >{{ item.label }}</span
                                                >
                                            </a>

                                            <a
                                                *ngIf="item.externalLink"
                                                [href]="item.externalLink"
                                                target="_blank"
                                                (click)="select.emit($event)"
                                            >
                                                <span
                                                    [ngClass]="{
                                                        'is-child': item.isChild,
                                                        'is-grandchild': item.isGrandChild
                                                    }"
                                                    >{{ item.label }}</span
                                                >
                                            </a>
                                        </li>
                                    </ng-container>
                                </ng-container>
                            </ng-container>
                        </ng-container>
                    </ng-container>
                    <ng-container *ngFor="let templateRef of appendItems">
                        <ng-container
                            *ngTemplateOutlet="templateRef; context: { eventEmitter: select }"
                        ></ng-container>
                    </ng-container>
                </ul>
            </div>
        </div>
    `,
    changeDetection: ChangeDetectionStrategy.OnPush,
    animations: [
        trigger('slide', [
            transition(':enter', [
                style({
                    transform: 'translateY(-100%)'
                }),
                animate(
                    '200ms cubic-bezier(0, 0.5, 0.5, 1)',
                    style({
                        transform: 'translateY(0)'
                    })
                )
            ]),
            transition(':leave', [
                animate(
                    '200ms cubic-bezier(0, 0.5, 0.5, 1)',
                    style({
                        transform: 'translateY(-100%)'
                    })
                )
            ])
        ])
    ]
})
export class LsqDropdownNavigationComponent implements OnChanges {
    public displayItems: NavigationItem[] = [];
    public isShowing = false;
    public selected: any;
    public dropdownType = DropdownType;
    public showTriangle = true;
    public triangleLeftPos: string;
    public triangleRightPos: string;
    public triangleMarginLeft: string;
    public checklistValue: any[];

    @Input() public items: NavigationItem[] = [];
    @Input() public triangle?:
        | 'left'
        | 'right'
        | 'center'
        | false
        | number
        | { left?: string; right?: string } = 'left';
    @Input() public type = DropdownType.Normal;
    @Input() public header?: string;
    @Input() public background?: 'dark' | 'light' | 'white' | 'none' = 'light';
    @Input() public isLoggedIn: boolean;
    @Input() public shouldAnimate = false;
    @Input() public prependItems: QueryList<TemplateRef<any>>;
    @Input() public appendItems: QueryList<TemplateRef<any>>;

    @Output() public select = new EventEmitter<any>();
    @Output() public showChange = new EventEmitter<boolean>();

    @Input()
    public get show(): boolean {
        return this.isShowing;
    }
    public set show(val: boolean) {
        this.isShowing = val;
        this.showChange.emit(this.isShowing);
    }

    public ngOnChanges(): void {
        this.buildList();

        if (this.triangle) {
            if (this.triangle === 'left') {
                this.triangleLeftPos = '2.2rem';
            }
            if (this.triangle === 'right') {
                this.triangleRightPos = '2.2rem';
            }
            if (this.triangle === 'center') {
                this.triangleLeftPos = '50%';
                this.triangleMarginLeft = '-0.85rem';
            }
            if (typeof this.triangle === 'number') {
                this.triangleLeftPos = this.triangle.toString() + 'rem';
            }
            if (this.triangle && typeof this.triangle === 'object') {
                if (this.triangle.left) {
                    this.triangleLeftPos = this.triangle.left;
                }
                if (this.triangle.right) {
                    this.triangleRightPos = this.triangle.right;
                }
            }
        } else {
            this.showTriangle = false;
        }
    }

    private buildList(): void {
        const displayItems: NavigationItem[] = [];

        function recursivelyAddItems(
            items: NavigationItem[],
            areChildren?: boolean,
            areGrandChildren?: boolean
        ): void {
            items.forEach((item) => {
                if (areChildren) {
                    item.isChild = true;
                }
                if (areGrandChildren) {
                    item.isGrandChild = true;
                }
                displayItems.push(item);
                if (item.children) {
                    recursivelyAddItems(item.children as NavigationItem[], true, areChildren);
                }
            });
        }

        // Create DropdownListItem objects out of string or number items
        this.displayItems = this.items.map(
            (item: any): NavigationItem => {
                if (typeof item === 'object' && item !== null) {
                    return item as NavigationItem;
                } else {
                    let label: string = item;
                    if (typeof item === 'number') {
                        label = item.toString();
                    }
                    return {
                        label,
                        value: item
                    } as NavigationItem;
                }
            }
        );

        // Pull child items out into the Items array
        recursivelyAddItems(this.displayItems);
        this.displayItems = [...displayItems];
    }

    public handleNormalSelect(val: any): void {
        this.selected = val;
        this.select.emit(val);
        this.show = false;
    }

    public handleCheckboxCheck(val: any[]): void {
        this.select.emit(val);
    }

    public createId(label: string, index: number, suffix: string = ''): string {
        label = label.replace(/\s/g, '-').toLowerCase();
        return label + (suffix ? '-' + suffix : '') + '--' + index.toString();
    }
}
